<template lang="pug">
app-main-content-layout(title='')
  .c-orgs-page
    //- Header Section
    section.c-orgs-page__header
      .c-orgs-page__header-content
        h1.c-orgs-page__heading Organizations
        p.c-orgs-page__subtitle Manage organization accounts and settings

    //- Table Section
    section.c-orgs-table-section
      CmsListToolbar(
        :tabs="viewTabs"
        :active-tab="currentView"
        v-model="textFilter"
        search-placeholder="Search organizations by name or email..."
        :sort-value="sortBy"
        :sort-options="sortOptions"
        @tab-change="handleTabChange"
        @search="searchFilter"
        @update:sortValue="sortBy = $event"
        @sort="sortTable"
      )

      //- Skeleton Loader
      template(v-if="loading")
        .c-skeleton-loader
          .c-skeleton-row(v-for="n in 10" :key="n")
            .c-skeleton-cell.c-skeleton-cell--name
            .c-skeleton-cell.c-skeleton-cell--email
            .c-skeleton-cell.c-skeleton-cell--onboarded
            .c-skeleton-cell.c-skeleton-cell--active

      //- Table
      el-table(
        v-else
        :data="items.items"
        @row-click="routeToOrg($event)"
        style="width: 100%"
        :row-class-name="getRowClassName"
      ).clickable-rows
        el-table-column(label="Name" min-width="260")
          template(#default="scope")
            .crm-person
              .crm-avatar
                img(v-if="scope.row.avatar" :src="scope.row.avatar" :alt="scope.row.name")
                template(v-else) {{ getInitials(scope.row.name) }}
              .crm-person__text
                span.crm-name {{ scope.row.name }}
                .crm-subtitle(v-if="scope.row.title") {{ scope.row.title }}

        el-table-column(label="Email" min-width="300")
          template(#default="scope")
            .crm-meta
              crm-icon.crm-meta__icon(name="mail")
              span.crm-meta__text {{ scope.row.email }}

        el-table-column(label="Onboarded" min-width="110")
          template(#default="scope")
            span.crm-badge.crm-badge--teal(v-if="scope.row.onboarded") Yes
            span.crm-badge.crm-badge--gray(v-else) No

        el-table-column(label="Approved" min-width="110" v-if="isPending")
          template(#default="scope")
            span.crm-badge.crm-badge--teal(v-if="scope.row.approved") Yes
            span.crm-badge.crm-badge--yellow(v-else) No

        el-table-column(label="Active" min-width="110")
          template(#default="scope")
            span.crm-badge.crm-badge--teal(v-if="scope.row.active")
              crm-icon(name="check-circle")
              | Active
            span.crm-badge.crm-badge--gray(v-else)
              crm-icon(name="clock")
              | Inactive

        el-table-column(label="Joined" min-width="150" v-if="currentView != 'Invited'")
          template(#default="scope")
            .crm-meta(v-if="scope.row.createdAt")
              crm-icon.crm-meta__icon(name="calendar")
              | {{ formatJoined(scope.row.createdAt) }}
            span.crm-empty(v-else) —

      //- Pagination
      .c-orgs-pagination(v-if="items.items.length > 0")
        .c-orgs-pagination__controls
          el-button.c-orgs-pagination__button(
            @click="handleCurrentChange(currentPage - 1)"
            :disabled="currentPage === 1 || loading"
          ) Prev
          .c-orgs-pagination__info
            span.c-orgs-pagination__page-number Page {{ currentPage }}
            span.c-orgs-pagination__item-count(v-if="items.items.length > 0") Showing {{ startItem }}-{{ endItem }}
          el-button.c-orgs-pagination__button(
            @click="handleCurrentChange(currentPage + 1)"
            :disabled="!hasMorePages || loading"
          ) Next

  dialog-add-user(
    v-if="dialog"
    v-model="dialog"
    :sending-invite="sendingInvite"
    @save-item="organizationInvite"
    :server-error="serverError"
    @close-dialog="dialog=false"
  )
</template>

<script>

import {
  defineComponent, ref,
  onMounted, reactive,
  computed,
} from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import shared from './shared.ts';
import DialogAddUser from './DialogAddUser.vue';
import CmsListToolbar from '@/components/CmsListToolbar.vue';
import CrmIcon from './CrmIcon.vue';

export default defineComponent({
  name: 'ManageContent_Organizations',
  components: { DialogAddUser, CmsListToolbar, CrmIcon },
  setup() {
    const {
      router,
      loading,
      getAllOrganizations,
      createOrganizationInvite,
      doGetInvites,
      getOrganizationsV2,
    } = shared();
    const route = useRoute();
    const store = useStore();
    const filter = ref('');
    const items = reactive({ items: [] });
    const invited = reactive({ items: [] });
    const allItems = reactive({ items: [] });
    const textFilter = ref('');
    const viewTabs = ['All', 'Pending', 'Approved', 'Declined', 'Archived', 'Invited']
      .map((tab) => ({ label: tab, value: tab }));
    const isFiltered = ref(false);
    const serverError = ref('');
    const sendingInvite = ref(false);
    const dialog = ref(false);
    const isPending = route.name === 'ManagePendingOrganizations';
    const test = 'pending';
    const sortBy = ref('');
    const sortOptions = [
      { label: 'Name (A-Z)', value: { field: 'Name', asc: true } },
      { label: 'Name (Z-A)', value: { field: 'Name', asc: false } },
      { label: 'Email (A-Z)', value: { field: 'EMail', asc: true } },
      { label: 'Email (Z-A)', value: { field: 'EMail', asc: false } },
    ];
    const selectedOrganization = computed(() => store.getters['organizations/GET_SELECTED_ORGANIZATION']);

    const currentView = ref('All');
    const totalItems = computed(() => {
      // Estimate total items based on current page and hasMorePages
      if (hasMorePages.value) {
        return currentPage.value * pageSize.value + 1;
      }
      return (currentPage.value - 1) * pageSize.value + items.items.length;
    });
    const baseInput = reactive({
      ids: null,
      entity: null,
      entityIds: null,
      isActive: null,
      statuses: null,
      query: null,
      page: 1,
      pageSize: 20,
      excludeUnclaimed: null,
      sortBy: null,
      sortAsc: null,
    });

    const currentPage = ref(1);
    const pageSize = ref(20);
    const hasMorePages = ref(true);

    function getRowClassName(row) {
      if (selectedOrganization.value && row.row.id === selectedOrganization.value.id) {
        return 'selected-row';
      }
      return '';
    }

    function routeToOrg(itm) {
      if (itm.onboarded) {
        localStorage.setItem('selectedOrganization', JSON.stringify(itm));
        store.dispatch('organizations/SET_SELECTED_ORGANIZATION', itm);
        router.push(`/manage/orgs/${itm.id}/edit`);
      } else {
        router.push(`/manage/invites/${itm.id}/info`);
      }
    }

    async function organizationInvite(item) {
      try {
        loading.value = true;
        sendingInvite.value = true;
        await createOrganizationInvite(item);
        dialog.value = false;
        items.items = await getAllOrganizations();
      } catch (err) {
        serverError.value = err.message;
      } finally {
        loading.value = false;
        sendingInvite.value = false;
      }
    }

    function getInitials(name) {
      return (name || '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('');
    }

    function formatJoined(date) {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      });
    }

    function showOnboardedStatus(item) {
      return item.onboarded ? 'Onboarded' : '--';
    }

    function showActiveStatus(item) {
      return item.active ? 'Yes' : 'No';
    }

    function getClass(currentItem) {
      if (currentItem === currentView.value) {
        return 'filter-active';
      }
      return 'filter-inactive';
    }

    async function findInvites() {
      try {
        loading.value = true;
        invited.items = await doGetInvites();
        loading.value = false;
      } catch (err) {
        console.error('Error finding invites:', err);
      }
    }

    async function findOrganizationsV2() {
      try {
        loading.value = true;
        const result = await getOrganizationsV2(baseInput);
        allItems.items = result;
        items.items = result;

        // Check if we have more pages by seeing if we got a full page of results
        hasMorePages.value = result.length === pageSize.value;

        loading.value = false;
      } catch (err) {
        loading.value = false;
      }
    }

    // ________function that filters invites by entity ________
    function filterInvites() {
      items.items = invited.items.filter((item) => item.entity === 'Organization');
    }

    async function sortTable() {
      if (sortBy.value && sortBy.value.value) {
        baseInput.sortBy = sortBy.value.value.field;
        baseInput.sortAsc = sortBy.value.value.asc;
        baseInput.page = 1;
        currentPage.value = 1;
        hasMorePages.value = true;

        if (currentView.value === 'Invited') {
          filterInvites();
        } else {
          await findOrganizationsV2();
        }
      }
    }

    // Computed properties for pagination display
    const startItem = computed(() => {
      const offset = (currentPage.value - 1) * pageSize.value;
      return offset + 1;
    });

    const endItem = computed(() => {
      const offset = (currentPage.value - 1) * pageSize.value;
      return offset + items.items.length;
    });

    const handleCurrentChange = (page) => {
      if (page < 1 || (page > currentPage.value && !hasMorePages.value)) {
        return;
      }
      currentPage.value = page;
      baseInput.page = page;

      // Preserve current sorting when changing pages
      if (sortBy.value && sortBy.value.value) {
        baseInput.sortBy = sortBy.value.value.field;
        baseInput.sortAsc = sortBy.value.value.asc;
      }

      if (currentView.value === 'Invited') {
        filterInvites();
      } else {
        findOrganizationsV2();
      }
    };

    // ________create function for search filter ________
    async function searchFilter() {
      baseInput.ids = null;
      baseInput.entity = null;
      baseInput.entityIds = null;
      baseInput.isActive = null;
      baseInput.statuses = currentView.value;
      baseInput.query = textFilter.value || null;
      baseInput.page = 1;
      currentPage.value = 1;
      hasMorePages.value = true;
      baseInput.excludeUnclaimed = currentView.value === 'Approved' ? true : null;

      // Preserve current sorting when searching
      if (sortBy.value && sortBy.value.value) {
        baseInput.sortBy = sortBy.value.value.field;
        baseInput.sortAsc = sortBy.value.value.asc;
      }

      if (textFilter.value === '') {
        isFiltered.value = false;
      } else {
        isFiltered.value = true;
      }

      if (currentView.value === 'Invited') {
        filterInvites();
      } else {
        await findOrganizationsV2();
      }
    }

    async function clearTextFilter() {
      textFilter.value = '';
      isFiltered.value = false;
      baseInput.query = null;
      currentPage.value = 1;
      hasMorePages.value = true;
      // Preserve excludeUnclaimed setting for Approved tab
      baseInput.excludeUnclaimed = currentView.value === 'Approved' ? true : null;

      // Preserve current sorting when clearing filter
      if (sortBy.value && sortBy.value.value) {
        baseInput.sortBy = sortBy.value.value.field;
        baseInput.sortAsc = sortBy.value.value.asc;
      }

      if (currentView.value === 'Invited') {
        filterInvites();
      } else {
        await findOrganizationsV2();
      }
    }

    async function headerFilter(filterBy) {
      currentView.value = filterBy;

      baseInput.ids = null;
      baseInput.entity = null;
      baseInput.entityIds = null;
      baseInput.isActive = null;
      baseInput.query = null;
      baseInput.page = 1;
      currentPage.value = 1;
      hasMorePages.value = true;

      // Preserve current sorting when changing filter
      if (sortBy.value && sortBy.value.value) {
        baseInput.sortBy = sortBy.value.value.field;
        baseInput.sortAsc = sortBy.value.value.asc;
      }

      if (filterBy === 'Invited') {
        filterInvites();
      } else {
        baseInput.statuses = filterBy;
        // Set excludeUnclaimed to true only for Approved tab
        baseInput.excludeUnclaimed = filterBy === 'Approved' ? true : null;
        await findOrganizationsV2();
      }
    }

    function handleTabChange(tabName) {
      headerFilter(tabName);
    }

    onMounted(async () => {
      // await findOrganizations(route.name);
      await findInvites();
      if (route.name === 'ManagePendingOrganizations') {
        currentView.value = 'Pending';
        baseInput.statuses = 'Pending';
      }
      await findOrganizationsV2();
    });

    return {
      // data (static)
      loading,
      filter,
      isPending,
      test,
      // data (reactive)
      items,
      serverError,
      dialog,
      sendingInvite,
      selectedOrganization,
      getRowClassName,
      getClass,
      headerFilter,
      currentView,
      invited,
      allItems,
      textFilter,
      viewTabs,
      isFiltered,
      currentPage,
      pageSize,
      hasMorePages,
      startItem,
      endItem,
      totalItems,
      // methods
      getInitials,
      formatJoined,
      handleCurrentChange,
      showOnboardedStatus,
      showActiveStatus,
      organizationInvite,
      routeToOrg,
      findInvites,
      searchFilter,
      clearTextFilter,
      sortBy,
      sortTable,
      sortOptions,
      baseInput,
      filterInvites,
      handleTabChange,
    };
  },
});
</script>

<style lang="scss" scoped>
@import './crm-list';

.c-orgs-page {
  @include crm-page;
}

@include crm-header('c-orgs-page');
@include crm-table('c-orgs-table-section');
@include crm-cells;
@include crm-skeleton;

.crm-person__text {
  display: flex;
  flex-direction: column;
}

.crm-subtitle {
  font-size: 0.75rem;
  line-height: 1rem;
  color: $gray-600;
}

// Prev / Next pagination
.c-orgs-pagination {
  padding: 1rem;
  border-top: 1px solid $border;

  &__controls {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
  }

  &__info {
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: $gray-600;
  }

  &__page-number {
    font-weight: 500;
    color: $foreground;
  }

  &__button.el-button {
    height: 32px;
    padding: 0 12px;
    border-radius: 6px;
    border: 1px solid $border;
    background: #fff;
    color: $foreground;
    font-family: $ui-font;
    font-size: 0.875rem;
    font-weight: 500;
    margin: 0;

    &:hover:not(:disabled) {
      background: rgba(236, 236, 240, 0.5);
      border-color: $border;
      color: $foreground;
    }

    &:disabled {
      opacity: 0.5;
    }
  }
}

:deep(.el-table .selected-row) {
  background-color: rgba(236, 236, 240, 0.5);
}

@media (max-width: 768px) {
  .c-orgs-page {
    padding: 1.5rem;
  }
}

@media (max-width: 480px) {
  .c-orgs-page {
    padding: 1rem;
  }
}
</style>
